import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, LogIn, ShieldX, Users, Mail, CreditCard } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

export default function Admin() {
  const { t } = useTranslation();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/overview`, { withCredentials: true })
      .then((r) => {
        setData(r.data);
        setState("ok");
      })
      .catch((e) => setState(e.response?.status === 401 ? "login" : "forbidden"));
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main data-testid="admin-page">
      <Header />
      <div className="pt-32 pb-24 max-w-5xl mx-auto px-6 lg:px-10 min-h-screen">
        <h1 className="font-display font-semibold tracking-tighter text-white text-3xl sm:text-4xl">{t("admin.title")}</h1>
        <p className="mt-2 text-sm text-white/50">{t("admin.subtitle")}</p>

        {state === "loading" && (
          <div className="mt-20 flex justify-center" data-testid="admin-loading">
            <Loader2 className="w-8 h-8 text-mint animate-spin" strokeWidth={1.5} />
          </div>
        )}

        {state === "login" && (
          <div data-testid="admin-login-prompt" className="mt-12 rounded-2xl border border-white/10 bg-card p-10 text-center">
            <LogIn className="w-10 h-10 text-mint mx-auto" strokeWidth={1.5} />
            <p className="mt-4 text-white/70">{t("admin.signinPrompt")}</p>
            <button onClick={login} data-testid="admin-login-button" className="mt-6 rounded-full bg-mint px-6 py-3 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105">
              {t("admin.signin")}
            </button>
          </div>
        )}

        {state === "forbidden" && (
          <div data-testid="admin-forbidden" className="mt-12 rounded-2xl border border-red-400/20 bg-card p-10 text-center">
            <ShieldX className="w-10 h-10 text-red-400 mx-auto" strokeWidth={1.5} />
            <p className="mt-4 text-white/70">{t("admin.forbidden")}</p>
          </div>
        )}

        {state === "ok" && data && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="mt-10 space-y-10">
            <div className="grid grid-cols-3 gap-4">
              {[
                { key: "leads", icon: Mail, value: data.stats.leads, id: "admin-stat-leads" },
                { key: "users", icon: Users, value: data.stats.users, id: "admin-stat-users" },
                { key: "subscribers", icon: CreditCard, value: data.stats.subscribers, id: "admin-stat-subscribers" },
              ].map((s) => (
                <div key={s.key} data-testid={s.id} className="rounded-2xl border border-white/8 bg-card p-5 lg:p-7">
                  <s.icon className="w-5 h-5 text-mint" strokeWidth={1.5} />
                  <p className="mt-3 font-display text-3xl font-semibold text-white">{s.value}</p>
                  <p className="text-xs text-white/45 mt-1">{t(`admin.stats.${s.key}`)}</p>
                </div>
              ))}
            </div>

            <section>
              <h2 className="font-display font-semibold text-white text-xl mb-4">{t("admin.sectionLeads")}</h2>
              <div className="rounded-2xl border border-white/8 overflow-hidden">
                <table className="w-full text-sm" data-testid="admin-leads-table">
                  <thead>
                    <tr className="bg-white/[0.04] text-left text-white/45 text-xs uppercase tracking-wider">
                      <th className="px-4 py-3">{t("admin.colEmail")}</th>
                      <th className="px-4 py-3 hidden sm:table-cell">{t("admin.colPhone")}</th>
                      <th className="px-4 py-3 hidden sm:table-cell">{t("admin.colLang")}</th>
                      <th className="px-4 py-3">{t("admin.colDate")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.leads.length === 0 && (
                      <tr><td colSpan="4" className="px-4 py-8 text-center text-white/35">{t("admin.noData")}</td></tr>
                    )}
                    {data.leads.map((l) => (
                      <tr key={l.email} className="border-t border-white/5 text-white/75">
                        <td className="px-4 py-3">{l.email}</td>
                        <td className="px-4 py-3 hidden sm:table-cell">{l.phone || "—"}</td>
                        <td className="px-4 py-3 hidden sm:table-cell uppercase">{l.language}</td>
                        <td className="px-4 py-3 text-white/45">{fmtDate(l.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="font-display font-semibold text-white text-xl mb-4">{t("admin.sectionSubs")}</h2>
              <div className="rounded-2xl border border-white/8 overflow-hidden">
                <table className="w-full text-sm" data-testid="admin-subs-table">
                  <thead>
                    <tr className="bg-white/[0.04] text-left text-white/45 text-xs uppercase tracking-wider">
                      <th className="px-4 py-3">{t("admin.colEmail")}</th>
                      <th className="px-4 py-3">{t("admin.colPlan")}</th>
                      <th className="px-4 py-3">{t("admin.colStatus")}</th>
                      <th className="px-4 py-3 hidden sm:table-cell">{t("admin.colDate")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.length === 0 && (
                      <tr><td colSpan="4" className="px-4 py-8 text-center text-white/35">{t("admin.noData")}</td></tr>
                    )}
                    {data.transactions.map((tx, i) => (
                      <tr key={i} className="border-t border-white/5 text-white/75">
                        <td className="px-4 py-3">{tx.customer_email || "—"}</td>
                        <td className="px-4 py-3">{(tx.lookup_key || "").replace("wadoo_", "")}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${tx.payment_status === "paid" ? "bg-mint/15 text-mint" : "bg-white/8 text-white/45"}`}>
                            {tx.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white/45 hidden sm:table-cell">{fmtDate(tx.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </motion.div>
        )}
      </div>
      <Footer />
    </main>
  );
}
